import type { CSSProperties } from 'react';
import { ServerOff } from 'lucide-react';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup, useGameGroupsStore } from '@/stores/gameGroups.store';
import { canRetry, serverFailureText, switchTargets, type ServerFailure } from '@/utils/supervision';
import styles from './ServerScopeNotice.module.scss';

interface ServerScopeNoticeProps {
  failure: ServerFailure;
  identifier: string;
  onRetry: () => void;
  onBack: () => void;
}

/**
 * Page Serveur en échec pour une raison de groupe de jeu : serveur d'un autre groupe (404 de
 * périmètre), groupe refusé par la passerelle, satellite du groupe injoignable. Remplace la fausse
 * console et les logs simulés d'avant, qui faisaient croire à une console vivante.
 *
 * « Ouvrir sur Iris » change le groupe du jeu : la page se remonte sur le même serveur avec le
 * nouveau groupe (clé de l'Outlet dans AdminLayout), sans rechargement complet.
 */
export function ServerScopeNotice({ failure, identifier, onRetry, onBack }: ServerScopeNoticeProps) {
  const gameId = useGameStore((s) => s.activeGameId);
  const setActiveGameGroup = useGameStore((s) => s.setActiveGameGroup);
  const active = useActiveGameGroup();
  const entry = useGameGroupsStore((s) => s.entries[gameId]);

  const { title, detail } = serverFailureText(failure, active?.label ?? null, identifier);
  const targets = switchTargets(failure, entry, active?.key ?? null);

  return (
    <div className={styles.notice} role="alert">
      <span className={styles.icon}><ServerOff size={40} strokeWidth={1.5} /></span>
      <h2 className={styles.title}>{title}</h2>
      <GameGroupBadge size="md" caption="Groupe consulté" />
      <p className={styles.detail}>{detail}</p>
      <div className={styles.actions}>
        {targets.map((g) => (
          <button
            key={g.key}
            type="button"
            className={styles.switchButton}
            style={g.color ? ({ '--group-color': g.color } as CSSProperties) : undefined}
            onClick={() => setActiveGameGroup(gameId, g.key)}
          >
            Ouvrir sur {g.label}
          </button>
        ))}
        {canRetry(failure) && (
          <button type="button" className={styles.secondaryButton} onClick={onRetry}>
            Réessayer
          </button>
        )}
        <button type="button" className={styles.secondaryButton} onClick={onBack}>
          Retour à la liste
        </button>
      </div>
    </div>
  );
}
